import React, { FC } from 'react'

import { RadioProps } from 'antd'
import styled from 'styled-components'
import { useSelector } from 'react-redux'

import { PaymentMethodEnum } from 'src/Constants/enums'
import { getResolvedTheme } from 'src/Redux/theme-process'
import PayPalLogoSmallIcon from 'src/Assets/Svg/pages-icons/paypall-small-logo.svg?react'
import { obfuscateEmail } from 'src/Helpers/layoutHelpers'
import { PrimaryRadio } from 'src/Components/PrimaryRadio'

import { PremiumCheckbox } from '../PremiumCheckbox'

import { SchedulePaymentMethodProps } from '.'

const SubText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  color: ${({ theme }) => theme.color.darkGrey};
`

const Text = styled.div<{ $isMonochrome?: boolean }>`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin-top: 4px;

  svg {
    display: block;
    width: 61px;
    height: auto;
  }

  ${({ $isMonochrome, theme }) =>
    $isMonochrome
      ? `
    svg path {
      fill: ${theme.color.darkGrey};
    }
  `
      : ''}
`

const StyledPrimaryRadio = styled(PrimaryRadio)`
  &.ant-radio-wrapper-disabled ${SubText} {
    display: none;
  }
  &.ant-radio-wrapper-disabled ${Text} {
    margin-top: 0;
  }
  &.ant-radio-wrapper-disabled svg {
    opacity: 0.4;
  }
`

const LabelContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`

const SavedContainer = styled.div`
  display: flex;
  height: 21px;
  padding-left: 6px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`

type Props = RadioProps &
  Pick<
    SchedulePaymentMethodProps,
    | 'hasSavePaypal'
    | 'onChangeHasSavePaypal'
    | 'paymentMethod'
    | 'paypalEmail'
    | 'isSavePaypalHidden'
  >
export const PaypalRadioButton: FC<Props> = (props) => {
  const {
    hasSavePaypal,
    paymentMethod,
    paypalEmail,
    value,
    onChangeHasSavePaypal,
    isSavePaypalHidden,
    ...restProps
  } = props

  const resolvedTheme = useSelector(getResolvedTheme)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (typeof onChangeHasSavePaypal !== 'function') {
      return
    }
    onChangeHasSavePaypal(e.target.checked)
  }

  const showSaveCheckbox =
    !isSavePaypalHidden &&
    paymentMethod === PaymentMethodEnum.PAYPAL &&
    !paypalEmail &&
    typeof onChangeHasSavePaypal === 'function'

  const subText = paypalEmail && obfuscateEmail(paypalEmail)

  return (
    <Container data-testid={'paypal-button'}>
      <StyledPrimaryRadio {...restProps} value={value}>
        <LabelContainer>
          <Text $isMonochrome={resolvedTheme === 'dark'}>
            <PayPalLogoSmallIcon />
          </Text>
          <SubText>{subText}</SubText>
        </LabelContainer>
      </StyledPrimaryRadio>
      {showSaveCheckbox && (
        <SavedContainer>
          <PremiumCheckbox
            name='save-paypal'
            onChange={handleChange}
            checked={hasSavePaypal}
          >
            Remember for future payments
          </PremiumCheckbox>
        </SavedContainer>
      )}
    </Container>
  )
}
